/**
 * Routes for the mobile app
 * Each section of entries correlates to a navigation stack, each needing a base path.
 *
 * This is split up into two items for easier understanding.
 *
 * Stacks -> Routes
 *
 * Stacks are the umbrella of other routes that can be navigated to, in order
 * to navigate to a specific screen you must specify the stack that relies on it.
 *
 * Routes are the individual screens that can be navigated to. There are no nested properties
 * here and we use a url format to help debug the routes.
 *
 * [See here for adding the proper values to React Navigation](apps/mobile-apps/src/routes/RouteParams.ts)
 */

const AuthRoutes = {
  AuthStack: 'AuthStack',
  AuthLogin: '/auth/login',
  AuthSignup: '/auth/signup',
  AuthForgotPassword: '/auth/forgot-password',
  AuthOnboarding: '/auth/onboarding',
  AuthMFA: '/auth/mfa',
} as const;

const LearnRoutes = {
  LearningCenterStack: 'LearningCenterStack',
  LearningCenter: '/learn',
  LearningCenterBlog: '/learn/blog',
  LearningCenterArticle: '/learn/blog/:articleId',
} as const;

const PropertiesRoutes = {
  PropertiesList: '/properties/list',
  PropertiesDetails: '/properties/details',
} as const;

const TransactionRoutes = {
  TransactionStack: 'TransactionStack',
  Transaction: '/transaction',
  TransactionDetails: '/transaction/details',
  TransactionDocuments: '/transaction/documents',
  TransactionSuccess: '/transaction/success',
} as const;

const PortfolioRoutes = {
  Portfolio: '/portfolio',
  PortfolioDetails: '/portfolio/details',
} as const;

const AccountRoutes = {
  Account: '/account',
  AccountAbout: '/account/about',
  AccountBasicInfo: '/account/profile/details',
  AccountDocuments: '/account/documents',
  AccountInvestmentAccountDetails: '/account/brokerage',
  AccountLearningCenter: '/account/learning-center',
  AccountLinkedBankAccount: '/account/bank',
  AccountLiveHelp: '/account/live-help',
  AccountProfile: '/account/profile',
  AccountSupport: '/account/support',
  AccountTransactionHistory: '/account/transaction-history',
  AccountTransactionDetails: '/account/transaction/details',
} as const;

// Generic screens that live under the root stack. Primarily universal modals.
const UtilityRoutes = {
  TabsStack: 'TabsStack',
  LearnHowItWorks: '/learn-how-it-works',
  MediaViewer: '/view-document',
  Onboarding: '/onboarding',
  BankLinking: '/bank-linking',
  VerifySSN: '/verify-ssn',
  Welcome: '/welcome',
  NotFound: 'NotFound',
} as const;

export const MobileRoutes = {
  ...AuthRoutes,
  ...LearnRoutes,
  ...PropertiesRoutes,
  ...TransactionRoutes,
  ...PortfolioRoutes,
  ...AccountRoutes,
  ...UtilityRoutes,
} as const;

export type MobileRoutes = typeof MobileRoutes;
export type MobileRoutesKeys = keyof MobileRoutes;
export type MobileRoute = MobileRoutes[MobileRoutesKeys];

/**
 * Breakdown specific routes for typing references
 */

type LearnRoutes = typeof LearnRoutes;
type PropertiesRoutes = typeof PropertiesRoutes;
type TransactionRoutes = typeof TransactionRoutes;
type PortfolioRoutes = typeof PortfolioRoutes;
type AccountRoutes = typeof AccountRoutes;
type UtilityRoutes = typeof UtilityRoutes;
